/**
 * HÜCRE IZGARASI — ana sayfa kart düzeni (Gelen kararlar 28.10.2026;
 * `claude/notlar/2026-10-28-ana-sayfa-kararlar.md` §3).
 *
 * - Kartlar 48 px'lik sabit kare hücrelerden oluşan bir ızgaraya yerleşir;
 *   sütun sayısı ekrana sığdığı kadardır.
 * - Her kart önce içeriğinin sığdığı en küçük hücre bloğunu alır (taban
 *   genişlik + o genişlikte içeriğin gerektirdiği satır).
 * - Sonra boş komşu hücrelere büyüyebilir: yatayda tabanın en çok %50,
 *   dikeyde o genişlikte içeriğin gerektirdiğinin en çok %25 fazlası.
 *   İçeriğine göre en az yer almış kart önce büyür, önce yana denenir.
 *   Yana genişleyen kartın içeriği yeniden ölçülür (listeler sütunlara
 *   yayılır, kart kısalabilir).
 * - Sınıra ulaşınca büyüme durur, kalan hücreler boş kalır.
 * - SIRA DEĞİŞMEZ: kartlar sırayla, satır satır yerleşir; sonraki kart
 *   öndeki bir boşluğa geri dönmez (Otomatik Fiyatlar görünüşte de en sonda —
 *   açık sorunun cevabı "evet").
 * - İki kart yan yana sığmıyorsa (telefon) ızgara yok: tek sütun, kart
 *   içeriği kadar uzun (`null`).
 *
 * Bu modül yalnızca hesaplar; ölçüm ve çizim `HucreIzgarasi` bileşeninde.
 */

/** Hücrenin kenarı (px). */
export const HUCRE = 48;
/** Hücreler arası boşluk (px). */
export const ARALIK = 8;
/** Kartın taban genişliği: eski düzenin en dar kartı (18rem). */
export const TABAN_PX = 288;

export const YATAY_PAY = 1.5;
export const DIKEY_PAY = 1.25;

/** `px` genişliği ya da yüksekliği kaplamak için gereken hücre sayısı. */
export function hucreSayisi(px: number): number {
  return Math.max(1, Math.ceil((px + ARALIK) / (HUCRE + ARALIK)));
}

/** `n` hücrelik bloğun piksel ölçüsü (aradaki boşluklar dahil). */
export function hucrePiksel(n: number): number {
  return n * HUCRE + (n - 1) * ARALIK;
}

/** Genişlikteki yerden sığan sütun sayısı. */
export function sutunSayisi(px: number): number {
  return Math.max(0, Math.floor((px + ARALIK) / (HUCRE + ARALIK)));
}

export interface IzgaraKarti {
  /** İçeriğin sığdığı en dar genişlik, hücre. */
  readonly taban: number;
  /** Verilen genişlikte (hücre) içeriğin gerektirdiği satır sayısı. */
  readonly satirIhtiyaci: (genislik: number) => number;
}

/** Kartın yeri; hepsi hücre, sıfırdan. */
export interface Yer {
  readonly sutun: number;
  readonly satir: number;
  readonly genislik: number;
  readonly yukseklik: number;
}

/**
 * Kartların yerleri; iki taban kart yan yana sığmıyorsa `null` (tek sütun).
 */
export function yerlesim(kartlar: readonly IzgaraKarti[], sutun: number): Yer[] | null {
  if (kartlar.length === 0) return [];
  const taban = kartlar.map((k) => Math.max(1, Math.min(sutun, k.taban)));
  if (sutun < 2 * Math.min(...taban)) return null;

  // Bellekli ihtiyaç: ölçüm pahalı (DOM), her genişlik bir kez.
  const bellek = kartlar.map(() => new Map<number, number>());
  const ihtiyac = (i: number, g: number): number => {
    const m = bellek[i]!;
    let v = m.get(g);
    if (v === undefined) {
      v = Math.max(1, Math.ceil(kartlar[i]!.satirIhtiyaci(g)));
      m.set(g, v);
    }
    return v;
  };

  const sahip: number[][] = [];
  const hucre = (r: number, c: number): number => sahip[r]?.[c] ?? -1;
  const isaretle = (y: Yer, deger: number): void => {
    for (let r = y.satir; r < y.satir + y.yukseklik; r++) {
      const satir = (sahip[r] ??= new Array<number>(sutun).fill(-1));
      for (let c = y.sutun; c < y.sutun + y.genislik; c++) satir[c] = deger;
    }
  };
  const bos = (y: Yer, haric: number): boolean => {
    if (y.sutun < 0 || y.sutun + y.genislik > sutun) return false;
    for (let r = y.satir; r < y.satir + y.yukseklik; r++)
      for (let c = y.sutun; c < y.sutun + y.genislik; c++) {
        const s = hucre(r, c);
        if (s !== -1 && s !== haric) return false;
      }
    return true;
  };

  // 1) Taban bloklarla sırayla yerleşim; imleç yalnızca ileri gider.
  const yerler: Yer[] = [];
  let imlecSatir = 0;
  let imlecSutun = 0;
  for (let i = 0; i < kartlar.length; i++) {
    const g = taban[i]!;
    const y0 = ihtiyac(i, g);
    let konan: Yer | null = null;
    for (let r = imlecSatir; konan === null; r++) {
      for (let c = r === imlecSatir ? imlecSutun : 0; c + g <= sutun; c++) {
        const aday = { sutun: c, satir: r, genislik: g, yukseklik: y0 };
        if (bos(aday, -1)) {
          konan = aday;
          break;
        }
      }
    }
    yerler.push(konan);
    isaretle(konan, i);
    imlecSatir = konan.satir;
    imlecSutun = konan.sutun + konan.genislik;
  }

  // 2) Büyüme: en az yer almış kart önce, önce yana.
  const tabanAlan = yerler.map((y) => y.genislik * y.yukseklik);
  const yerDegistir = (i: number, yeni: Yer): void => {
    isaretle(yerler[i]!, -1);
    yerler[i] = yeni;
    isaretle(yeni, i);
  };
  const yanaBuyu = (i: number): boolean => {
    const y = yerler[i]!;
    const g = y.genislik + 1;
    if (g > Math.floor(taban[i]! * YATAY_PAY)) return false;
    // Genişleyen kart yeni içerik boyuna iner (içinde boşluk kalmasın);
    // dikey pay gerekirse ayrıca, sırası gelince verilir.
    const yeni = { ...y, genislik: g, yukseklik: ihtiyac(i, g) };
    if (!bos(yeni, i)) return false;
    yerDegistir(i, yeni);
    return true;
  };
  const asagiBuyu = (i: number): boolean => {
    const y = yerler[i]!;
    const gerek = ihtiyac(i, y.genislik);
    if (y.yukseklik + 1 > Math.floor(gerek * DIKEY_PAY)) return false;
    const yeni = { ...y, yukseklik: y.yukseklik + 1 };
    if (!bos(yeni, i)) return false;
    yerDegistir(i, yeni);
    return true;
  };
  // Her adım bir kartın genişliğini ya da (aynı genişlikte) yüksekliğini
  // sınırlı bir tavana doğru artırır: döngü biter. Sigorta yine de var.
  for (let adim = 0; adim < 10_000; adim++) {
    const sira = yerler
      .map((y, i) => ({ i, oran: (y.genislik * y.yukseklik) / tabanAlan[i]! }))
      .sort((a, b) => a.oran - b.oran || a.i - b.i);
    if (!sira.some(({ i }) => yanaBuyu(i) || asagiBuyu(i))) break;
  }
  return yerler;
}
